import { ReactNode } from "react";
import { useVenturePledges } from "@project-mgmt/feature-ventures";
import { PledgeListItem, type PledgeWithBacker } from "./PledgeListItem";
import { LoadingSpinner } from "../LoadingSpinner";
import type { VentureStatus } from "@project-mgmt/feature-ventures";

interface PledgesPanelProps {
  ventureId: string;
  ventureStatus: VentureStatus;
  isOwner: boolean;
  workspaceId: string;
  onMakePledge: () => void;
}

/**
 * PledgesPanel component displays the list of pledges for a venture
 * with summary information and pledge action button.
 *
 * Features:
 * - Fetches pledges using useVenturePledges hook
 * - Displays backer count in header
 * - Shows "Make a Pledge" button (conditionally)
 * - Handles loading, error, and empty states
 * - Sorts pledges by timestamp (newest first)
 * - Responsive layout for mobile devices
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 4.1, 4.4, 6.4
 */
export function PledgesPanel({
  ventureId,
  ventureStatus: _ventureStatus,
  isOwner: _isOwner,
  workspaceId,
  onMakePledge,
}: PledgesPanelProps): ReactNode {
  // Fetch pledges data
  const { pledges, isLoading, error } = useVenturePledges(workspaceId, ventureId);

  // Transform pledges to include backer name (API returns backerName field)
  const pledgesWithBacker: PledgeWithBacker[] = pledges.map((pledge) => ({
    ...pledge,
    backerName: (pledge as any).backerName || null,
  }));

  // Sort pledges by pledgedAt in descending order (newest first)
  const sortedPledges = [...pledgesWithBacker].sort((a, b) => {
    return new Date(b.pledgedAt).getTime() - new Date(a.pledgedAt).getTime();
  });

  return (
    <div className="pledges-panel py-4 sm:py-6">
      {/* Header with backer count and action button */}
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <h2 className="text-xl font-semibold text-black sm:text-2xl">Backers ({pledgesWithBacker.length})</h2>

        <button
          onClick={onMakePledge}
          className="min-h-[44px] w-full rounded-lg bg-black px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 sm:w-auto sm:px-6 sm:py-3 sm:text-base"
          aria-label="Make a pledge to this venture"
        >
          Make a Pledge
        </button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="md" message="Loading pledges..." />
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <div className="mb-2 text-lg font-medium text-red-900">Failed to load pledges</div>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && sortedPledges.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-12 text-center sm:py-16">
          {/* Money bag emoji icon */}
          <div className="mb-3 text-5xl sm:mb-4 sm:text-6xl" aria-hidden="true">
            💰
          </div>

          <h3 className="mb-2 text-lg font-semibold text-black sm:text-xl">No pledges yet</h3>

          <p className="mb-4 text-sm text-gray-600 sm:mb-6 sm:text-base">Be the first to back this venture!</p>

          <button
            onClick={onMakePledge}
            className="min-h-[44px] w-full rounded-lg bg-black px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 sm:w-auto sm:px-6 sm:py-3 sm:text-base"
            aria-label="Be the first backer"
          >
            Be the First Backer
          </button>
        </div>
      )}

      {/* Pledge list */}
      {!isLoading && !error && sortedPledges.length > 0 && (
        <div className="pledge-list flex flex-col gap-3">
          {sortedPledges.map((pledge) => (
            <PledgeListItem key={pledge.id} pledge={pledge} />
          ))}
        </div>
      )}
    </div>
  );
}
